/**
 * Stage C — Guided Start: the one surface for bringing a parked vehicle into a
 * bay and starting work, on desktop, tablet and phone alike.
 *
 *   countdown   "Moving Judy Bloom’s 2003 Grand Prix from Spot 12 to
 *               Bay 4 — Josh’s Bay" · Starting in 5… · [Cancel] [Different bay]
 *               [Start now]. Nothing moves and nothing starts until it commits.
 *   choose      when OptiBay cannot safely propose a bay (none planned, the
 *               planned bay is occupied / unavailable / was just refused) or the
 *               technician asked for a different one. Nothing is preselected.
 *               Item #14: the shop floor MAP is the primary selector; the
 *               List is a toggle, and the only view when the shop's bays are
 *               not laid out. Both share one selection; both return a bay id.
 *   found       #19: a fresh exact camera sighting puts the parked car in a
 *               usable empty bay — "Found it in Bay 3" · [Change] [Start].
 *               One tap; no countdown (a camera answer is confirmed, never
 *               auto-accepted). Change opens the ordinary chooser.
 *   mismatch    the car is in a bay, a different bay is planned. Both facts are
 *               shown; the technician starts where the car IS, or leaves it to
 *               be moved first. No countdown: nothing is inferred here.
 *
 * Presentational only: the decision comes from guidedStart.ts, the commit goes
 * through the Stage A executor into the ONE atomic pull_in_and_start_my_timer.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FacilityPickerMap } from '@/components/location/FacilityPickerMap';
import { pickerLayoutUsable, type FacilityLayout } from '@/lib/location/facilityLayout';
import {
  GUIDED_START_COUNTDOWN_SECONDS,
  GuidedCountdown,
  bayMismatchMessage,
  chooseBayMessage,
  guidedStartPreview,
  type GuidedBayChoice,
  type GuidedStartDecision,
} from '@/lib/timer/interaction/guidedStart';

export type GuidedStartMode = 'countdown' | 'choose' | 'mismatch' | 'found';

export interface GuidedStartDialogProps {
  open: boolean;
  mode: GuidedStartMode;
  /** MOVE_PLANNED (countdown), CHOOSE_BAY (choose), BAY_MISMATCH (mismatch) or CAMERA_FOUND (found). */
  decision: Extract<GuidedStartDecision, { kind: 'MOVE_PLANNED' | 'CHOOSE_BAY' | 'BAY_MISMATCH' | 'CAMERA_FOUND' }> | null;
  choices: readonly GuidedBayChoice[];
  vehicleLabel: string | null;
  /** The shop floor (item #14). Null/unusable: the List is the selector. */
  layout?: FacilityLayout | null;
  busy: boolean;
  countdownSeconds?: number;
  /** Commit the move-and-start into this bay. Called at most once per countdown. */
  onCommit: (bayId: string) => void;
  onCancel: () => void;
  onChooseDifferent: () => void;
  /** Mismatch: start where the vehicle is now (no move). */
  onStartHere?: () => void;
  /** Mismatch: nothing now — the vehicle is to be moved to its planned bay first. */
  onMoveFirst?: () => void;
  /**
   * Oct 20 (D-4): the act this bay choice belongs to. 'resume' is the guided Resume of parked paused work
   * (pull_in_and_resume_my_timer); only the words change. Default 'start'.
   */
  act?: 'start' | 'resume';
}

export function GuidedStartDialog({
  open,
  mode,
  decision,
  choices,
  vehicleLabel,
  layout = null,
  busy,
  countdownSeconds = GUIDED_START_COUNTDOWN_SECONDS,
  onCommit,
  onCancel,
  onChooseDifferent,
  onStartHere,
  onMoveFirst,
  act = 'start',
}: GuidedStartDialogProps) {
  const verb = act === 'resume' ? 'Resume' : 'Start';
  const busyWords = act === 'resume' ? 'Resuming…' : 'Starting…';
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);
  const [selectedBayId, setSelectedBayId] = useState('');
  // Map first; the technician's choice of view is kept across re-asks.
  const [view, setView] = useState<'map' | 'list'>('map');
  const mapUsable = pickerLayoutUsable(layout, choices);
  const showMap = mapUsable && view === 'map';
  const countdown = useRef<GuidedCountdown | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const countingTo = open && mode === 'countdown' && decision?.kind === 'MOVE_PLANNED' ? decision.toBayId : null;

  // The countdown is UX, not authority: it only decides WHEN to submit once.
  useEffect(() => {
    if (!countingTo) return undefined;
    const c = new GuidedCountdown(countdownSeconds, () => commitRef.current(countingTo));
    countdown.current = c;
    setSecondsLeft(countdownSeconds);
    const id = window.setInterval(() => setSecondsLeft(c.tick()), 1000);
    return () => {
      window.clearInterval(id);
      c.cancel();
      countdown.current = null;
    };
  }, [countingTo, countdownSeconds]);

  // Every time the chooser opens, it starts from nothing selected.
  useEffect(() => {
    if (open && mode === 'choose') setSelectedBayId('');
  }, [open, mode, decision]);

  const stopCountdown = () => countdown.current?.cancel();
  const cancel = () => { stopCountdown(); onCancel(); };
  const different = () => { stopCountdown(); onChooseDifferent(); };

  // Never "its current location": with nothing recorded the decision says so.
  const fromLabel = (decision && 'fromLabel' in decision ? decision.fromLabel : null) ?? 'where it is';
  const selected = choices.find((c) => c.id === selectedBayId);
  // #19 — highlighted, never preselected: the technician still chooses.
  const suggestedChoice = choices.find((c) => c.suggested && !c.occupied && !c.refused) ?? null;
  // A bay that is occupied or was just refused is never selectable (map or list).
  const selectable = (c: (typeof choices)[number]) => !c.occupied && !c.refused;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
      {/* Core gate: a shop with many bays made the chooser taller than a phone
          or landscape tablet, pushing "Move & Start" off-screen. Same bound
          the Pause and Stop dialogs use. */}
      <DialogContent
        className={cn('max-h-[90vh] overflow-y-auto', mapUsable && mode !== 'mismatch' ? 'max-w-2xl' : 'max-w-md')}
        data-testid="guided-start-dialog"
      >
        {mode === 'mismatch' && decision?.kind === 'BAY_MISMATCH' ? (
          <>
            <DialogHeader>
              <DialogTitle>Not in its planned bay</DialogTitle>
              <DialogDescription data-testid="guided-start-mismatch">
                {bayMismatchMessage(decision, vehicleLabel)}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={cancel} disabled={busy}>Cancel</Button>
              {decision.plannedState === 'AVAILABLE' && (
                <Button variant="outline" onClick={() => onMoveFirst?.()} disabled={busy}
                        data-testid="guided-start-move-first">
                  Move it to {decision.plannedLabel} first
                </Button>
              )}
              <Button onClick={() => onStartHere?.()} disabled={busy} data-testid="guided-start-here">
                {busy ? 'Starting…' : `Start in ${decision.currentLabel}`}
              </Button>
            </DialogFooter>
          </>
        ) : mode === 'found' && decision?.kind === 'CAMERA_FOUND' ? (
          <>
            <DialogHeader>
              <DialogTitle>{act === 'resume' ? 'Resuming work' : 'Starting work'}</DialogTitle>
              <DialogDescription data-testid="guided-start-found">Found it in {decision.label}.</DialogDescription>
            </DialogHeader>
            {mapUsable && layout && (
              <FacilityPickerMap
                layout={layout}
                choices={choices}
                selectedBayId={decision.bayId}
                focusBayId={decision.bayId}
                className="h-[clamp(200px,40dvh,420px)]"
              />
            )}
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={cancel} disabled={busy}>Cancel</Button>
              <Button variant="outline" onClick={different} disabled={busy} data-testid="guided-start-found-change">Change</Button>
              <Button onClick={() => onCommit(decision.bayId)} disabled={busy} data-testid="guided-start-found-start">
                {busy ? busyWords : verb}
              </Button>
            </DialogFooter>
          </>
        ) : mode === 'countdown' && decision?.kind === 'MOVE_PLANNED' ? (
          <>
            <DialogHeader>
              <DialogTitle>{act === 'resume' ? 'Resuming work' : 'Starting work'}</DialogTitle>
              <DialogDescription data-testid="guided-start-preview">{decision.preview}.</DialogDescription>
            </DialogHeader>
            {mapUsable && layout && (
              // Where it is going, on the floor — shown, not chosen here.
              <FacilityPickerMap
                layout={layout}
                choices={choices}
                selectedBayId={null}
                focusBayId={decision.toBayId}
                className="h-[clamp(200px,40dvh,420px)]"
              />
            )}
            <p className="text-center text-3xl font-semibold tabular-nums" data-testid="guided-start-countdown"
               aria-live="polite">
              {busy ? busyWords : `${act === 'resume' ? 'Resuming' : 'Starting'} in ${secondsLeft}…`}
            </p>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={cancel} disabled={busy}>Cancel</Button>
              <Button variant="outline" onClick={different} disabled={busy}>Different bay</Button>
              <Button onClick={() => countdown.current?.now()} disabled={busy}>{verb} now</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Which bay?</DialogTitle>
              <DialogDescription data-testid="guided-start-reason">
                {act === 'resume'
                  ? 'The vehicle is parked. Choose the bay to bring it into — your paused work resumes there.'
                  : decision?.kind === 'CHOOSE_BAY'
                  ? chooseBayMessage(decision)
                  : 'Choose the bay to bring it into.'}
              </DialogDescription>
              {suggestedChoice && (
                <p className="text-sm font-medium" data-testid="guided-start-suggested">
                  Looks like it&apos;s in {suggestedChoice.label}.
                </p>
              )}
            </DialogHeader>
            {mapUsable && (
              <div className="flex gap-1 self-start rounded-md border p-0.5" role="group" aria-label="Show bays as">
                {(['map', 'list'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    data-testid={`guided-start-view-${v}`}
                    onClick={() => setView(v)}
                    className={cn('min-h-9 rounded px-3 text-sm', view === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}
                  >
                    {v === 'map' ? 'Map' : 'List'}
                  </button>
                ))}
              </div>
            )}
            {showMap && layout ? (
              <FacilityPickerMap
                layout={layout}
                choices={choices}
                selectedBayId={selectedBayId || null}
                onSelect={setSelectedBayId}
                focusBayId={suggestedChoice?.id ?? (decision?.kind === 'CHOOSE_BAY' ? decision.plannedBayId : null)}
                disabled={busy}
              />
            ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Bays" data-testid="guided-start-list">
              {choices.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={selectedBayId === c.id}
                  disabled={!selectable(c) || busy}
                  data-testid={`guided-bay-${c.id}`}
                  onClick={() => setSelectedBayId(c.id)}
                  className={cn(
                    'rounded-md border p-3 text-left text-sm transition-colors',
                    selectedBayId === c.id ? 'border-primary bg-primary/10' : c.suggested ? 'border-primary/60 border-dashed hover:bg-muted' : 'hover:bg-muted',
                    !selectable(c) && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span className="block font-medium">{c.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {c.occupied ? 'Occupied' : c.refused ? 'Just refused' : c.suggested ? 'Looks like it’s here' : c.planned ? 'Planned' : 'Free'}
                  </span>
                </button>
              ))}
              {choices.length === 0 && (
                <p className="col-span-full text-sm text-muted-foreground">No bay can take a vehicle right now.</p>
              )}
            </div>
            )}
            {selected && (
              <p className="text-sm text-muted-foreground" data-testid="guided-start-choice-preview">
                {guidedStartPreview(vehicleLabel, fromLabel, selected.label)}.
              </p>
            )}
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={cancel} disabled={busy}>Cancel</Button>
              <Button onClick={() => selectedBayId && onCommit(selectedBayId)}
                      disabled={!selectedBayId || busy} data-testid="guided-start-confirm">
                {busy ? busyWords : `Move & ${verb}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
